import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, LayoutDashboard, Compass, Sparkles, MessageSquare } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';

export const Header = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Single role-aware nav target (no double "Admin" badge)
  const dashboardLink = (() => {
    if (!user) return null;
    if (user.role === 'guest') return { to: '/my-bookings', label: t('nav.myTrips'), icon: Compass, testid: 'my-bookings-link' };
    if (user.role === 'hotel') return { to: '/hotel/dashboard', label: t('nav.extranet'), icon: LayoutDashboard, testid: 'hotel-dashboard-link' };
    if (user.role === 'admin') return { to: '/admin/dashboard', label: t('nav.adminConsole'), icon: LayoutDashboard, testid: 'admin-dashboard-link' };
    return null;
  })();

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        {/* Wordmark */}
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-black tracking-tight text-black">
            local<span className="text-[#FF0059]">liink</span><span className="text-black">.com</span>
          </h1>
        </Link>

        {/* Primary Nav (TripAdvisor-style pills, only on lg+) */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
          <Link to="/" className="px-4 py-2 rounded-full font-bold text-sm text-black hover:bg-gray-100 transition-colors flex items-center gap-1.5">
            <Compass className="h-4 w-4" /> {t('nav.discover')}
          </Link>
          <Link to="/insights" className="px-4 py-2 rounded-full font-bold text-sm text-black hover:bg-gray-100 transition-colors flex items-center gap-1.5" data-testid="nav-insights-link">
            <Sparkles className="h-4 w-4" /> {t('nav.insights')}
          </Link>
          <Link to="/reviews" className="px-4 py-2 rounded-full font-bold text-sm text-black hover:bg-gray-100 transition-colors flex items-center gap-1.5" data-testid="nav-reviews-link">
            <MessageSquare className="h-4 w-4" /> {t('nav.reviews')}
          </Link>
        </nav>

        {/* Right Cluster */}
        <div className="flex items-center gap-2 md:gap-3">
          <LanguageSwitcher />
          {user ? (
            <>
              {dashboardLink && (
                <Link
                  to={dashboardLink.to}
                  className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-sm text-black hover:bg-gray-100 transition-colors"
                  data-testid={dashboardLink.testid}
                >
                  <dashboardLink.icon className="h-4 w-4" />
                  {dashboardLink.label}
                </Link>
              )}
              {(user.role === 'guest' || user.role === 'hotel') ? (
                <Link
                  to="/profile"
                  className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F2F2F2] hover:bg-[#FFE5EE] transition-colors"
                  data-testid="profile-link"
                >
                  <div className="w-7 h-7 rounded-full bg-[#FF0059] text-white flex items-center justify-center text-xs font-black">
                    {(user.name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-bold text-black truncate max-w-[120px]">{user.name}</span>
                </Link>
              ) : (
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F2F2F2]" data-testid="user-info">
                  <div className="w-7 h-7 rounded-full bg-[#FF0059] text-white flex items-center justify-center text-xs font-black">
                    {(user.name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-bold text-black truncate max-w-[120px]">{user.name}</span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-black hover:bg-gray-800 text-white text-sm font-bold transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">{t('nav.logout')}</span>
              </button>
            </>
          ) : (
            <>
              <Link
                to="/list-your-property"
                className="hidden sm:inline-flex px-4 py-2 rounded-full font-bold text-sm text-black hover:bg-gray-100 transition-colors"
                data-testid="list-property-link"
              >
                {t('nav.listProperty')}
              </Link>
              <Link
                to="/login"
                className="px-5 py-2 rounded-full bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold text-sm transition-colors"
                data-testid="login-link"
              >
                {t('nav.signIn')}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
